/**
 * ConfirmPublishModal Component
 *
 * A confirmation modal displayed before publishing a Survey Project.
 * It ensures users validate key setup steps and acknowledge any potential risks
 * (survey dates vs term end, disabled email notifications) before proceeding.
 *
 * Major warnings (up to two): survey ends before term; no email settings enabled.
 * A single acknowledgement applies when any major warning is shown.
 */

import { Box, Checkbox, Stack, Typography } from '@mui/material'
import { useTheme, type Theme } from '@mui/material/styles'
import React, { useState } from 'react'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { useQuery } from '@tanstack/react-query'
import { TermsManager } from '../../../../APIManager'
import LoadingPage from '../../../../pages/LoadingPage'
import { WarningBannerAlert } from '../../../SurveyFeature/styles'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { yellow } from '../../../../colors'
import { type ISlot } from '../../../../models/DistributionPlan'
import { buildMajorWarnings, type MajorWarning } from './majorWarnings'

interface ConfirmPublishModalProps {
  handleCloseModal: () => void
  onConfirm: () => void
  slot: ISlot | undefined
}

const checkboxItems = [
  { key: 'template', label: 'The survey template is up to date and ready to be sent.' },
  { key: 'instructors', label: 'Instructors are correctly assigned to course sections.' },
  { key: 'enrollment', label: 'Students are correctly enrolled in course sections.' },
  { key: 'surveyDates', label: 'Survey dates are configured correctly for course sections.' },
  { key: 'attributes', label: '(If applicable) Attributes are added to the correct course sections.' }
] as const

function MajorWarningSections ({
  warnings,
  multiple,
  theme
}: {
  warnings: MajorWarning[]
  multiple: boolean
  theme: Theme
}): JSX.Element {
  const spacing = theme.spacing(3.5)

  if (!multiple) {
    const w = warnings[0]
    return (
      <>
        <Typography fontWeight='bold' fontSize={spacing}>
          {w.singlePrimaryHeading}
        </Typography>
        {w.detailLines.map((line) => (
          <Typography key={line} fontSize={spacing} mt={theme.spacing(3)}>
            {line}
          </Typography>
        ))}
        <Typography fontSize={spacing} mt={theme.spacing(3)}>
          <Typography fontSize={spacing} fontWeight='bold' component='span'>Recommended:</Typography>
          {' '}
          {w.recommended}
        </Typography>
      </>
    )
  }

  return (
    <Stack component='div' spacing={3} sx={{ mt: 1 }}>
      {warnings.map((w, index) => (
        <Box key={w.id} display='flex' gap={1.5} alignItems='flex-start'>
          <Typography
            component='span'
            fontWeight='bold'
            fontSize={spacing}
            sx={{ flexShrink: 0, lineHeight: 1.35, minWidth: '1.5em' }}
          >
            {index + 1}.
          </Typography>
          <Box flex={1} minWidth={0}>
            <Typography fontWeight='bold' fontSize={spacing} component='div'>
              {w.listTitle}
            </Typography>
            {w.detailLines.map((line) => (
              <Typography key={line} fontSize={spacing} mt={theme.spacing(2)} component='div'>
                {line}
              </Typography>
            ))}
            <Typography fontSize={spacing} mt={theme.spacing(2)} component='div'>
              <Typography fontSize={spacing} fontWeight='bold' component='span'>Recommended:</Typography>
              {' '}
              {w.recommended}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  )
}

export const ConfirmPublishModal: React.FC<ConfirmPublishModalProps> = ({
  handleCloseModal,
  onConfirm,
  slot
}) => {
  const theme = useTheme()
  type checkboxKeys = typeof checkboxItems[number]['key']
  const [checkedItems, setCheckedItems] = useState<Record<checkboxKeys, boolean>>({
    template: false,
    instructors: false,
    enrollment: false,
    surveyDates: false,
    attributes: false
  })
  const allChecked = Object.values(checkedItems).every(Boolean)

  const { data: term, isLoading, isError } = useQuery({
    queryKey: ['getTermById', slot?.term?.termId],
    enabled: !!slot?.term?.termId,
    queryFn: async () => {
      const termId = slot?.term?.termId
      if (!termId) return null
      const response = await TermsManager.getTermById({ termId: termId ?? '' })
      return response?.data?.data
    }
  })

  const majorWarnings = buildMajorWarnings(slot, term?.endTimestamp)
  const hasMajorWarnings = majorWarnings.length > 0
  const multipleMajorWarnings = majorWarnings.length > 1

  const [isMajorWarningsAcknowledged, setIsMajorWarningsAcknowledged] = useState(false)

  const handleCheckboxChange = (key: checkboxKeys): void => {
    const updatedCheckedItems = { ...checkedItems, [key]: !checkedItems[key] }
    setCheckedItems(updatedCheckedItems)
  }

  const warningHeadline = multipleMajorWarnings
    ? `Warning! ${majorWarnings.length} potential issues found`
    : 'Warning!'

  const confirmDisabled =
    !allChecked || (hasMajorWarnings && !isMajorWarningsAcknowledged)

  if (isLoading) return <LoadingPage />
  if (isError) return <Typography color='error'>Error loading term data. Please try again.</Typography>
  if (!slot) return <Typography color='error'>Unable to load survey project.</Typography>

  return (
    <Box>
      <Typography fontStyle='italic'>
        Before publishing your Survey Project, confirm the following:
      </Typography>

      {checkboxItems.map(({ key, label }) => (
        <Box key={key} display='flex' alignItems='center' m={theme.spacing(2)}>
          <Checkbox
            checked={checkedItems[key] ?? false}
            onChange={() => { handleCheckboxChange(key) }}
          />
          <Typography fontSize={theme.spacing(3.5)}>{label}</Typography>
        </Box>
      ))}

      {hasMajorWarnings && (
        <Box m={theme.spacing(2)}>
          <WarningBannerAlert
            severity='warning'
            icon={<WarningAmberIcon sx={{ color: yellow[600], fontSize: '22px' }} />}
          >
            <Box display='flex' flexDirection='column' alignItems='stretch' width='100%' gap={1}>
              <Typography fontWeight='bold' component='div'>{warningHeadline}</Typography>
              <MajorWarningSections
                warnings={majorWarnings}
                multiple={multipleMajorWarnings}
                theme={theme}
              />
              <Box width='100%' display='flex' flexDirection='row' alignItems='center' justifyContent='flex-end' mt={1}>
                <Checkbox
                  checked={isMajorWarningsAcknowledged}
                  onChange={() => { setIsMajorWarningsAcknowledged(!isMajorWarningsAcknowledged) }}
                />
                <Typography fontSize={theme.spacing(3.5)} fontWeight='bold'>
                  I understand and want to continue.
                </Typography>
              </Box>
            </Box>
          </WarningBannerAlert>
        </Box>
      )}

      <DefaultModalActions
        destructiveButtonProps={{ onClick: handleCloseModal }}
        confirmButtonProps={{ onClick: onConfirm, disabled: confirmDisabled }}
      />
    </Box>
  )
}
